import {
  ANALYTICS_EVENT,
  IAnalyticsConfiguration,
  type IAnalyticsIntegrationResponse,
  IntegrationName,
} from '@kouto/types';
import {
  AnalyticsClient,
  type IAnalyticsDataPayload,
} from 'features/analytics/types/analytics';
import { debug } from 'utils/debug';
import { Ga4EventsFormatter } from './events.formatter';

type Ga4Config = Partial<Omit<IAnalyticsConfiguration, 'initializeSdk'>> & {
  enabled?: boolean;
};

export class Ga4Client extends AnalyticsClient {
  private readonly config: Ga4Config;

  readonly provider = IntegrationName.GA4_ANALYTICS;

  constructor(response: IAnalyticsIntegrationResponse) {
    super();
    this.config = response.config ?? {};
  }

  async init() {
    await this.runWhenAvailable();

    this.ready = true;

    this.events.forEach((event) =>
      this.sendAnalyticsEvent(event.eventName, event.payload),
    );
  }

  hasEvent() {
    return !!this.config?.enabled;
  }

  // eslint-disable-next-line class-methods-use-this
  isAvailable(): boolean {
    return !!window.dataLayer;
  }

  private sendAnalyticsEvent(
    eventName: ANALYTICS_EVENT,
    payload: IAnalyticsDataPayload,
  ) {
    if (!this.hasEvent()) {
      return;
    }

    const isView = eventName.startsWith('view_');

    const eventProperties = Ga4EventsFormatter(eventName, payload);

    if (!eventProperties) {
      return;
    }

    delete eventProperties?.products;

    window.dataLayer?.push({ ecommerce: null });

    window.dataLayer?.push(eventProperties);

    debug('log', this.provider, {
      type: isView ? 'View Event triggered' : 'Link Event triggered',
      eventName,
      ga4Event: eventProperties.event,
      eventProperties,
      eventConfig: this.config,
    });
  }

  sendEvent(eventName: ANALYTICS_EVENT, payload: IAnalyticsDataPayload): void {
    if (this.config && !this.hasEvent()) {
      return;
    }
    if (!this.ready) {
      this.events.push({
        eventName,
        payload,
      });
      return;
    }

    this.sendAnalyticsEvent(eventName, payload);
  }
}

declare global {
  interface Window {
    dataLayer?: object[];
  }
}
